import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { useAuth, startLogin } from "@/lib/auth";
import { renderMarkdown } from "@/lib/markdown";
import { Plus, Edit3, Trash2, Check, X, Eye, Save, FileText, Inbox, Mail, Package, Award, MessageSquare, Sparkles, Search, Archive, ArchiveRestore, ChevronDown, Map as MapIcon, LayoutDashboard, Languages, Settings2 } from "lucide-react";
import ItemsTab from "@/pages/AdminItems";
import BadgesTab from "@/pages/AdminBadges";
import RoadmapTab from "@/pages/AdminRoadmap";
import OverviewTab from "@/pages/AdminOverview";
import TranslationsTab from "@/pages/AdminTranslations";
import SettingsTab from "@/pages/AdminSettings";

export function AdminGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="text-center py-32 font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow" data-testid="admin-loading">// verifying clearance...</div>;
  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="admin-login-required">
        <h1 className="font-display text-3xl text-[var(--t-cyan)] uppercase mb-4">Restricted Zone</h1>
        <p className="text-[var(--t-text-dim)] mb-6">Sign in with your Discord account to access the admin console. The <strong className="text-[var(--t-cyan)]">Wiki Editor</strong> role in the Territoria Discord grants access.</p>
        <button onClick={startLogin} className="btn-primary" data-testid="admin-sign-in">Sign in with Discord</button>
      </div>
    );
  }
  if (!user.is_admin) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="admin-not-authorized">
        <h1 className="font-display text-3xl text-[var(--t-red)] uppercase mb-4">Access Denied</h1>
        <p className="text-[var(--t-text-dim)]">Your account is signed in as <span className="text-[var(--t-cyan)] font-mono">{user.email}</span> but does not have admin clearance.</p>
      </div>
    );
  }
  return children;
}

export default function Admin() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (key) => {
    const sp = new URLSearchParams(params);
    sp.set("tab", key);
    setParams(sp);
  };
  return (
    <AdminGuard>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10" data-testid="admin-page">
        <div className="flex items-end justify-between mb-6 md:mb-8 gap-4">
          <div>
            <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// Command Console</span>
            <h1 className="font-display text-3xl md:text-5xl mt-2 text-[var(--t-text)] uppercase">Admin</h1>
          </div>
        </div>

        <div className="flex gap-1 md:gap-2 border-b border-[var(--t-border)] mb-6 md:mb-8 overflow-x-auto" data-testid="admin-tabs">
          {[
            { key: "overview", label: "Overview", icon: LayoutDashboard },
            { key: "pages", label: "Wiki Pages", icon: FileText },
            { key: "items", label: "Items", icon: Package },
            { key: "badges", label: "Badges", icon: Award },
            { key: "roadmap", label: "Roadmap", icon: MapIcon },
            { key: "translations", label: "Languages", icon: Languages },
            { key: "settings", label: "Settings", icon: Settings2 },
            { key: "forum", label: "Forum", icon: MessageSquare },
            { key: "contributions", label: "Contributions", icon: Inbox },
            { key: "newsletter", label: "Newsletter", icon: Mail },
          ].map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 md:px-4 py-2.5 md:py-3 flex items-center gap-1.5 md:gap-2 font-display text-xs md:text-sm uppercase tracking-[0.1em] md:tracking-[0.12em] whitespace-nowrap transition ${tab === t.key ? "text-[var(--t-cyan)] border-b-2 border-[var(--t-blue)]" : "text-[var(--t-text-dim)] hover:text-[var(--t-blue-glow)]"}`} data-testid={`tab-${t.key}`}>
                <Icon className="w-3.5 h-3.5 md:w-4 md:h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "overview" && <OverviewTab />}
        {tab === "pages" && <PagesTab />}
        {tab === "items" && <ItemsTab />}
        {tab === "badges" && <BadgesTab />}
        {tab === "roadmap" && <RoadmapTab />}
        {tab === "translations" && <TranslationsTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "forum" && <ForumModerationTab />}
        {tab === "contributions" && <ContributionsTab />}
        {tab === "newsletter" && <NewsletterTab />}
      </div>
    </AdminGuard>
  );
}

// Canonical wiki categories — keep in sync with frontend visibility logic and
// the v4 PDF rebuild in backend/server.py.
// "Archive" is a reserved bucket hidden from the public listing.
const KNOWN_CATEGORIES = [
  "Getting Started",
  "Character & Progression",
  "Economy & Industry",
  "Politics & Government",
  "Warfare & Military",
  "Items & Equipment",
  "Reference Database",
  "Archive",
];

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function PagesTab() {
  const [pages, setPages] = useState([]);
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const navigate = useNavigate();

  const load = () => client.get("/wiki/pages").then(r => setPages(r.data));
  useEffect(() => { load(); }, []);

  const del = async (slug) => {
    if (!window.confirm(`Permanently delete page "${slug}"? This cannot be undone.`)) return;
    await client.delete(`/wiki/pages/${slug}`);
    load();
  };

  const archive = async (page) => {
    const target = page.category === "Archive" ? "Reference Database" : "Archive";
    const verb = target === "Archive" ? "archive (hide from public)" : "restore (default category: Reference Database)";
    if (!window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} "${page.title}"?`)) return;
    await client.post("/wiki/pages", { ...page, category: target });
    load();
  };

  // Group by category for organized display
  const filtered = pages.filter(p => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (p.title || "").toLowerCase().includes(q) ||
      (p.slug || "").toLowerCase().includes(q) ||
      (p.category || "").toLowerCase().includes(q) ||
      (p.summary || "").toLowerCase().includes(q)
    );
  });

  const grouped = filtered.reduce((acc, p) => {
    const cat = p.category || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});
  // Stable category order: known first, Archive last, anything else in between
  const orderedCats = [
    ...KNOWN_CATEGORIES.filter(c => c !== "Archive" && grouped[c]),
    ...Object.keys(grouped).filter(c => !KNOWN_CATEGORIES.includes(c)).sort(),
    ...(grouped["Archive"] ? ["Archive"] : []),
  ];

  // sort entries inside each group by order
  for (const cat of orderedCats) {
    grouped[cat].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  return (
    <div data-testid="pages-tab">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-[var(--t-text-dim)]">{pages.length} pages · {filtered.length} shown</p>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--t-text-muted)] pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search pages…"
              data-testid="pages-search"
              className="field !py-1.5 !pl-8 !pr-3 !text-xs w-56"
            />
          </div>
        </div>
        <button onClick={() => navigate("/admin/new")} className="btn-primary !py-2 !px-4 !text-xs" data-testid="new-page-btn">
          <Plus className="w-4 h-4" /> New Page
        </button>
      </div>

      <div className="space-y-4">
        {orderedCats.map(cat => {
          const isCollapsed = collapsed[cat];
          const isArchive = cat === "Archive";
          return (
            <div key={cat} className="panel overflow-hidden" data-testid={`pages-group-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
              <button
                onClick={() => setCollapsed(c => ({ ...c, [cat]: !c[cat] }))}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--t-border)] bg-gradient-to-r from-[rgba(58,160,255,0.06)] to-transparent hover:bg-[rgba(58,160,255,0.09)] transition"
                data-testid={`pages-group-toggle-${cat.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">//</span>
                  <h3 className="font-display text-base md:text-lg uppercase tracking-[0.04em] text-[var(--t-text)] truncate">{cat}</h3>
                  {isArchive && (
                    <span className="font-mono text-[9px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase px-1.5 py-0.5 border border-[var(--t-border)]">Hidden</span>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.2em] uppercase">{grouped[cat].length}</span>
                  <ChevronDown className={`w-4 h-4 text-[var(--t-text-muted)] transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                </div>
              </button>
              {!isCollapsed && (
                <div className="divide-y divide-[var(--t-border)]">
                  {grouped[cat].map(p => (
                    <div key={p.slug} className="flex items-center justify-between p-3 md:p-4 hover:bg-[rgba(58,160,255,0.04)] gap-3" data-testid={`admin-page-${p.slug}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-display text-sm md:text-base text-[var(--t-cyan)] uppercase leading-tight">{p.title}</div>
                        <div className="font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.15em] mt-1 truncate">/{p.slug} · order {p.order}</div>
                      </div>
                      <div className="flex gap-1.5 md:gap-2 flex-shrink-0">
                        <Link to={`/wiki/${p.slug}`} target="_blank" className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid={`view-${p.slug}`} title="View live"><Eye className="w-3 h-3" /></Link>
                        <Link to={`/admin/edit/${p.slug}`} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid={`edit-${p.slug}`} title="Edit"><Edit3 className="w-3 h-3" /></Link>
                        <button onClick={() => archive(p)} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid={`archive-${p.slug}`} title={isArchive ? "Restore page" : "Archive (hide from public)"}>
                          {isArchive ? <ArchiveRestore className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                        </button>
                        <button onClick={() => del(p.slug)} className="btn-ghost !py-1.5 !px-3 !text-[10px] !border-[var(--t-red)] !text-[var(--t-red)]" data-testid={`delete-${p.slug}`} title="Delete permanently"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {orderedCats.length === 0 && (
          <div className="panel p-10 text-center text-[var(--t-text-muted)] font-mono text-xs tracking-[0.2em] uppercase">// no pages match your search</div>
        )}
      </div>
    </div>
  );
}

export function PageEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug;
  const [page, setPage] = useState({ slug: "", title: "", category: "Reference Database", order: 100, summary: "", content: "", language: "en" });
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!isNew) {
      client.get(`/wiki/pages/${slug}`).then(r => { setPage(r.data); setSlugTouched(true); });
    }
  }, [slug, isNew]);

  // Auto-fill slug from title when creating a new page (until user manually edits the slug)
  const onTitleChange = (e) => {
    const t = e.target.value;
    setPage(prev => ({
      ...prev,
      title: t,
      slug: !slugTouched && isNew ? slugify(t) : prev.slug,
    }));
  };

  const save = async () => {
    if (!page.title?.trim()) { alert("Title is required."); return; }
    if (!page.slug?.trim()) { alert("Slug is required."); return; }
    setSaving(true);
    try {
      await client.post("/wiki/pages", { ...page, slug: page.slug.trim(), title: page.title.trim() });
      navigate("/admin");
    } catch (e) {
      alert("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  // Allow free-form category but suggest the known ones via a datalist
  const categoryListId = "wiki-category-suggestions";

  return (
    <AdminGuard>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-10" data-testid="page-editor">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-[10px] md:text-xs font-mono tracking-[0.2em] text-[var(--t-blue-glow)] uppercase hover:text-[var(--t-cyan)] mb-1" data-testid="editor-back">
              ← Admin
            </Link>
            <h1 className="font-display text-2xl md:text-3xl text-[var(--t-text)] uppercase">{isNew ? "New Entry" : "Edit Entry"}</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setPreview(!preview)} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="toggle-preview">
              <Eye className="w-3.5 h-3.5" /> {preview ? "Edit" : "Preview"}
            </button>
            <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-4 !text-xs" data-testid="save-page">
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        {!preview ? (
          <div className="panel p-4 md:p-6 space-y-4">
            <div className="grid md:grid-cols-[1fr_1fr_120px] gap-3 md:gap-4">
              <div>
                <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Slug</label>
                <input
                  className="field"
                  value={page.slug}
                  onChange={e => { setPage({ ...page, slug: slugify(e.target.value) }); setSlugTouched(true); }}
                  disabled={!isNew}
                  data-testid="editor-slug"
                  placeholder="my-page-slug"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Category</label>
                <input
                  className="field"
                  list={categoryListId}
                  value={page.category}
                  onChange={e => setPage({ ...page, category: e.target.value })}
                  data-testid="editor-category"
                  placeholder="Reference Database"
                />
                <datalist id={categoryListId}>
                  {KNOWN_CATEGORIES.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Order</label>
                <input className="field" type="number" value={page.order} onChange={e => setPage({ ...page, order: parseInt(e.target.value) || 0 })} data-testid="editor-order" />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Title</label>
              <input className="field" value={page.title} onChange={onTitleChange} data-testid="editor-title" placeholder="Page title" />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Summary</label>
              <input className="field" value={page.summary} onChange={e => setPage({ ...page, summary: e.target.value })} data-testid="editor-summary" placeholder="One-line summary that appears in directory listings" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">Content (Markdown)</label>
                <span className="font-mono text-[9px] text-[var(--t-text-muted)] tracking-[0.15em]">## headings · **bold** · [links](url) · - lists · tables</span>
              </div>
              <textarea className="field font-mono text-sm" rows={20} value={page.content} onChange={e => setPage({ ...page, content: e.target.value })} data-testid="editor-content" placeholder="## Section Title\n\nWrite the page body in Markdown..." />
            </div>
          </div>
        ) : (
          <div className="panel p-6 md:p-8">
            <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-2">// Preview · {page.category}</div>
            <h1 className="font-display text-2xl md:text-3xl text-[var(--t-text)] uppercase mb-2">{page.title || "(no title)"}</h1>
            {page.summary && <p className="text-sm text-[var(--t-text-dim)] mb-6">{page.summary}</p>}
            <div className="prose-t" dangerouslySetInnerHTML={{ __html: renderMarkdown(page.content) }} />
          </div>
        )}
      </div>
    </AdminGuard>
  );
}

function ContributionsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => client.get("/contributions", { params: { status: filter || undefined } }).then(r => setItems(r.data));
  useEffect(() => { load(); }, [filter]);

  const update = async (id, status) => {
    await client.patch(`/contributions/${id}`, { status });
    load();
  };

  return (
    <div data-testid="contributions-tab">
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected"].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`btn-ghost !py-1.5 !px-3 !text-xs ${filter === s ? "!border-[var(--t-blue)] !text-[var(--t-cyan)] !bg-[rgba(58,160,255,0.1)]" : ""}`} data-testid={`contrib-filter-${s}`}>{s}</button>
        ))}
      </div>
      <div className="space-y-3">
        {items.map(c => (
          <div key={c.id} className="panel p-5" data-testid={`contrib-${c.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-1">{c.page_slug ? `/${c.page_slug}` : "general"} · {c.author_name} · {new Date(c.created_at).toLocaleString()}</div>
                <h3 className="font-display text-lg text-[var(--t-cyan)] uppercase mb-2">{c.title}</h3>
                <div className="prose-t text-sm" dangerouslySetInnerHTML={{ __html: renderMarkdown(c.body) }} />
                {c.author_email && <div className="font-mono text-xs text-[var(--t-text-muted)] mt-2">{c.author_email}</div>}
              </div>
              {filter === "pending" && (
                <div className="flex flex-col gap-2">
                  <button onClick={() => update(c.id, "approved")} className="btn-primary !py-1.5 !px-3 !text-[10px]" data-testid={`approve-${c.id}`}><Check className="w-3 h-3" /></button>
                  <button onClick={() => update(c.id, "rejected")} className="btn-ghost !py-1.5 !px-3 !text-[10px] !border-[var(--t-red)] !text-[var(--t-red)]" data-testid={`reject-${c.id}`}><X className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="panel p-10 text-center text-[var(--t-text-muted)] font-mono text-xs tracking-[0.2em] uppercase">// inbox clear</div>}
      </div>
    </div>
  );
}

function ForumModerationTab() {
  const [groups, setGroups] = useState([]);
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => {
    const params = filter ? { params: { category: filter } } : {};
    client.get("/forum/posts", params).then(r => setPosts(r.data));
  };
  useEffect(() => {
    client.get("/forum/categories").then(r => setGroups(r.data));
  }, []);
  useEffect(() => { load(); }, [filter]);

  const allChannels = groups.flatMap(g => g.channels.map(c => ({ ...c, group_name: g.name })));

  const promote = async (id) => {
    if (!window.confirm("Promote this thread into the official wiki?")) return;
    setBusy(id);
    try {
      const r = await client.post(`/forum/posts/${id}/promote`);
      alert(`Promoted to /wiki/${r.data.slug}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    } finally { setBusy(""); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this thread and all its replies?")) return;
    setBusy(id);
    try {
      await client.delete(`/forum/posts/${id}`);
      load();
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="forum-mod-tab">
      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter("")} className={`btn-ghost !py-1.5 !px-3 !text-xs ${!filter ? "!border-[var(--t-blue)] !text-[var(--t-cyan)] !bg-[rgba(58,160,255,0.1)]" : ""}`} data-testid="forum-mod-filter-all">All</button>
        {allChannels.map(c => (
          <button key={c.slug} onClick={() => setFilter(c.slug)} className={`btn-ghost !py-1.5 !px-3 !text-xs ${filter === c.slug ? "!border-[var(--t-blue)] !text-[var(--t-cyan)] !bg-[rgba(58,160,255,0.1)]" : ""}`} data-testid={`forum-mod-filter-${c.slug}`} title={c.group_name}>{c.name}</button>
        ))}
      </div>
      <div className="space-y-3">
        {posts.map(p => (
          <div key={p.id} className="panel p-5" data-testid={`forum-mod-post-${p.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-1">
                  /{p.category} · {p.author_name} · {new Date(p.created_at).toLocaleString()} · {p.reply_count || 0} replies
                  {p.promoted_slug && <span className="ml-2 inline-flex items-center gap-1 text-[var(--t-green)]"><Sparkles className="w-3 h-3" /> in codex</span>}
                </div>
                <h3 className="font-display text-lg text-[var(--t-cyan)] uppercase mb-2 leading-tight">{p.title}</h3>
                <div className="text-sm text-[var(--t-text-dim)] whitespace-pre-wrap line-clamp-3">{p.body.slice(0, 400)}{p.body.length > 400 ? "…" : ""}</div>
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                <Link to={`/forum/${p.id}`} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid={`forum-mod-view-${p.id}`}><Eye className="w-3 h-3" /> View</Link>
                {!p.promoted_slug && (
                  <button onClick={() => promote(p.id)} disabled={busy === p.id} className="btn-primary !py-1.5 !px-3 !text-[10px]" data-testid={`forum-mod-promote-${p.id}`}>
                    <Sparkles className="w-3 h-3" /> Promote
                  </button>
                )}
                <button onClick={() => del(p.id)} disabled={busy === p.id} className="btn-ghost !py-1.5 !px-3 !text-[10px] !border-[var(--t-red)] !text-[var(--t-red)]" data-testid={`forum-mod-delete-${p.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {posts.length === 0 && <div className="panel p-10 text-center text-[var(--t-text-muted)] font-mono text-xs tracking-[0.2em] uppercase">// no threads</div>}
      </div>
    </div>
  );
}

function NewsletterTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { client.get("/newsletter").then(r => setItems(r.data)); }, []);
  return (
    <div data-testid="newsletter-tab">
      <p className="text-sm text-[var(--t-text-dim)] mb-4">{items.length} subscribers · download or paste into your mailer.</p>
      <div className="panel p-5">
        <div className="font-mono text-sm space-y-1 max-h-[600px] overflow-y-auto">
          {items.map(i => (
            <div key={i.email} className="flex justify-between border-b border-[var(--t-border)] py-1.5">
              <span className="text-[var(--t-cyan)]">{i.email}</span>
              <span className="text-[var(--t-text-muted)] text-xs">{new Date(i.created_at).toLocaleDateString()}</span>
            </div>
          ))}
          {items.length === 0 && <div className="text-center text-[var(--t-text-muted)] tracking-[0.2em] uppercase py-6">// no subscribers yet</div>}
        </div>
      </div>
    </div>
  );
}
